<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/utils/axios'

const router = useRouter()
const notifications = ref([])
const unreadCount = ref(0)
const isOpen = ref(false)
const loading = ref(false)

// Fetch notifications
const fetchNotifications = async () => {
  try {
    loading.value = true
    const { data } = await api.get('/notifications')
    notifications.value = data
    unreadCount.value = data.filter(n => !n.read_at).length
  } catch (error) {
    console.error('Failed to fetch notifications:', error)
  } finally {
    loading.value = false
  }
}

// Mark as read and navigate
const handleNotificationClick = async (notification) => {
  try {
    if (!notification.read_at) {
      await api.patch(`/notifications/${notification.id}/read`)
      notification.read_at = new Date().toISOString()
      unreadCount.value--
    }
    
    // Close dropdown and navigate
    isOpen.value = false
    if (notification.data?.url) {
      router.push(notification.data.url)
    }
  } catch (error) {
    console.error('Failed to mark notification as read:', error)
  }
}

// Format date
const formatDate = (dateString) => {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

onMounted(() => {
  fetchNotifications()
  // Optional: Set up polling for new notifications
  const pollInterval = setInterval(fetchNotifications, 30000) // every 30s
  
  onUnmounted(() => {
    clearInterval(pollInterval)
  })
})
</script>

<template>
  <div class="relative">
    <!-- Notification Button with Badge -->
    <button 
      @click="isOpen = !isOpen"
      class="relative p-2 text-white pt-3 transition"
    >
      <i class="fa-solid fa-bell text-xl"></i>
      
      <!-- Red Dot Badge -->
      <span 
        v-if="unreadCount > 0"
        class="absolute -top-[2px] -right-1 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center"
      >
        {{ unreadCount }}
      </span>
    </button>

    <!-- Notifications Dropdown -->
    <div 
      v-if="isOpen"
      class="absolute top-12 right-0 w-96 max-h-[80vh] overflow-y-auto bg-white rounded-md shadow-xl z-50"
    >
      <div class="p-4 ">
        <h3 class="font-semibold">Notifikasi</h3>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="p-4 text-center text-gray-500">
        Loading...
      </div>

      <!-- Empty State -->
      <div v-else-if="notifications.length === 0" class="p-4 text-center text-gray-500">
        Tidak ada notifikasi
      </div>

      <!-- Notifications List -->
      <div v-else class="divide-gray-300 divide-y">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          
          class="p-4 hover:bg-blue-50 cursor-pointer transition"
          :class="{ 'bg-white': !notification.read_at }"
        >
          <div class="font-medium text-gray-900">
            {{ notification.data.title }}
          </div>
          <div class="text-sm text-gray-600 mt-1">
            {{ notification.data.message }}
          </div>
          <div class="text-xs text-gray-500 mt-2 text-end">
            {{ formatDate(notification.created_at) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.notifications-dropdown {
  max-height: 80vh;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #fff;
}

.notifications-dropdown::-webkit-scrollbar {
  width: 6px;
}

.notifications-dropdown::-webkit-scrollbar-track {
  background: #fff;
}

/* .notifications-dropdown::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 3px;
} */
</style>